import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Loader2, Lock } from "lucide-react";

const STATUSES = ["LIVE", "IGNITING", "COMING", "SEALED"];
const TIER_LABELS = {
  1: "Auto-canner",
  2: "Smart-auto",
  3: "Warden",
  4: "Founder",
};

/* -------------------------------------------------------------------------- */
/* Login                                                                       */
/* -------------------------------------------------------------------------- */

function LoginView({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/login", { email, password });
      toast.success("Welcome, Founder.");
      onLogin();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#050505]">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm border border-white/10 bg-[#0c0c0e] p-8"
      >
        <div className="flex items-center gap-2 mb-2">
          <Lock className="w-4 h-4 text-[#ff5a00]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#ff5a00]">
            Master Room
          </span>
        </div>
        <h1 className="font-display text-3xl text-[#ededed] mb-8">The Forge Admin</h1>
        <div className="space-y-4">
          <Input
            data-testid="admin-email"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-transparent border-white/15"
            required
          />
          <Input
            data-testid="admin-password"
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="bg-transparent border-white/15"
            required
          />
          <Button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="w-full bg-[#ff5a00] text-[#050505] hover:bg-[#ff8f00] font-mono uppercase text-xs tracking-widest"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enter"}
          </Button>
        </div>
      </form>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Reusable                                                                    */
/* -------------------------------------------------------------------------- */

function StatCard({ label, value, testid }) {
  return (
    <div className="border border-white/10 bg-[#0c0c0e] p-6" data-testid={testid}>
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#52525b] mb-2">
        {label}
      </p>
      <p className="font-display text-4xl text-[#ededed]">{value}</p>
    </div>
  );
}

function SectionKicker({ children }) {
  return (
    <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#ff5a00] mb-4">
      {children}
    </p>
  );
}

/* -------------------------------------------------------------------------- */
/* Film Control                                                                */
/* -------------------------------------------------------------------------- */

function FilmControl() {
  const [film, setFilm] = useState({ status: "empty", url: null });
  const [firing, setFiring] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/film/current");
      setFilm(data);
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  const fire = async () => {
    setFiring(true);
    try {
      const { data } = await api.post("/admin/film/generate");
      toast.success(data.message || "Bake started.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start bake.");
    } finally {
      setFiring(false);
    }
  };

  const statusColor =
    { ready: "#2DD4BF", generating: "#ff8f00", error: "#8B0000" }[film.status] ||
    "#52525b";

  return (
    <div data-testid="admin-film-control">
      <SectionKicker>The twelve-world filmstrip</SectionKicker>
      <div className="border border-white/10 bg-[#0c0c0e] p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
        <div className="flex items-center gap-3">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: statusColor }} />
          <span
            className="font-mono text-xs uppercase tracking-wider"
            style={{ color: statusColor }}
            data-testid="admin-film-status"
          >
            {film.status}
          </span>
          {film.url && (
            <a
              href={film.url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-[11px] text-[#a1a1aa] hover:text-[#ff5a00] underline"
            >
              view current film
            </a>
          )}
          {film.error && (
            <span className="font-mono text-[11px] text-[#8B0000]">{film.error}</span>
          )}
        </div>
        <Button
          data-testid="admin-film-generate"
          onClick={fire}
          disabled={firing || film.status === "generating"}
          className="bg-[#ff5a00] text-[#050505] hover:bg-[#ff8f00] font-mono uppercase text-[11px] tracking-widest"
        >
          {film.status === "generating" ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Forging…
            </>
          ) : (
            "Forge the film"
          )}
        </Button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Pledge Slider                                                               */
/* -------------------------------------------------------------------------- */

function PledgeControl() {
  const [value, setValue] = useState(10);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/pledge");
        setValue(data.value || 10);
      } catch {
        /* noop */
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/pledge", { value });
      toast.success(`Pledge set to ${value}%`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not update pledge");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-pledge-control">
      <SectionKicker>Pledge percentage (public)</SectionKicker>
      <div className="border border-white/10 bg-[#0c0c0e] p-6">
        <div className="flex items-baseline gap-3 mb-6">
          <span className="font-display text-5xl text-[#ff5a00]" data-testid="admin-pledge-value">
            {value}%
          </span>
          <span className="font-mono text-[11px] uppercase tracking-widest text-[#52525b]">
            of net revenue to philanthropy
          </span>
        </div>
        <Slider
          data-testid="admin-pledge-slider"
          value={[value]}
          onValueChange={([v]) => setValue(v)}
          min={10}
          max={100}
          step={1}
          className="mb-6"
        />
        <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-[#52525b] mb-6">
          <span>10% floor (birth-scroll bound)</span>
          <span>100% max</span>
        </div>
        <Button
          data-testid="admin-pledge-save"
          onClick={save}
          disabled={saving}
          className="bg-[#ff5a00] text-[#050505] hover:bg-[#ff8f00] font-mono uppercase text-[11px] tracking-widest"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save pledge"}
        </Button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Orders + Attribution                                                        */
/* -------------------------------------------------------------------------- */

function OrdersLog() {
  const [data, setData] = useState({ orders: [], attribution: [], captured_count: 0, captured_gross_usd: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orders");
      setData(data);
    } catch {
      /* noop */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-8" data-testid="admin-orders">
      <div>
        <div className="flex items-center justify-between mb-4">
          <SectionKicker>Paid orders</SectionKicker>
          <Button
            data-testid="admin-orders-refresh"
            onClick={load}
            variant="outline"
            className="border-white/15 text-[#a1a1aa] hover:text-[#ededed] font-mono uppercase text-[10px] tracking-widest"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Refresh"}
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-4 mb-6">
          <StatCard label="Captured orders" value={data.captured_count} testid="admin-orders-count" />
          <StatCard
            label="Captured gross (USD)"
            value={`$${data.captured_gross_usd.toFixed(2)}`}
            testid="admin-orders-gross"
          />
          <StatCard label="Total visits" value={data.visits_total || 0} testid="admin-orders-visits" />
        </div>
        <div className="border border-white/10 divide-y divide-white/[0.07] max-h-[420px] overflow-auto">
          {data.orders.length === 0 && (
            <p className="p-4 font-mono text-xs text-[#52525b]">
              No orders yet. This log fills as PayPal captures land.
            </p>
          )}
          {data.orders.map((o) => (
            <div
              key={o.paypal_id || o.id}
              data-testid={`admin-order-${o.paypal_id || o.id}`}
              className="p-4 bg-[#0c0c0e] flex flex-wrap items-center gap-4 text-sm"
            >
              <span
                className="font-mono text-[10px] uppercase tracking-wider px-2 py-1 border rounded"
                style={{
                  color: o.status === "COMPLETED" ? "#2DD4BF" : "#ff8f00",
                  borderColor: (o.status === "COMPLETED" ? "#2DD4BF" : "#ff8f00") + "60",
                }}
              >
                {o.status}
              </span>
              <span className="font-display text-lg text-[#ededed]">
                ${(o.amount || 0).toFixed(2)}
              </span>
              <span className="font-mono text-[11px] text-[#a1a1aa] flex-1 truncate">
                {(o.items || []).map((it) => it.title || it.slug).join(" + ") || "—"}
              </span>
              {o.ref && (
                <span className="font-mono text-[10px] uppercase tracking-wider text-[#ff5a00] border border-[#ff5a00]/40 rounded px-2 py-0.5">
                  ref: {o.ref}
                </span>
              )}
              <span className="font-mono text-[10px] text-[#52525b]">
                {o.captured_at ? new Date(o.captured_at).toLocaleString() : "—"}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <SectionKicker>Attribution — visits · orders · conversion</SectionKicker>
        <div className="border border-white/10 divide-y divide-white/[0.07]" data-testid="admin-attribution">
          {data.attribution.length === 0 && (
            <p className="p-4 font-mono text-xs text-[#52525b]">No attribution data yet.</p>
          )}
          {data.attribution.map((row) => (
            <div
              key={row.ref}
              data-testid={`admin-attribution-${row.ref}`}
              className="p-4 bg-[#0c0c0e]"
            >
              <div className="flex items-center justify-between gap-4 mb-2">
                <span className="font-mono text-sm text-[#ededed]">{row.ref}</span>
                <div className="flex items-center gap-6">
                  <span className="font-mono text-xs text-[#a1a1aa]">
                    {row.visits} visits
                  </span>
                  <span className="font-mono text-xs text-[#a1a1aa]">
                    {row.orders} orders
                  </span>
                  <span
                    className="font-mono text-xs px-2 py-0.5 border rounded"
                    style={{
                      color:
                        row.conversion_rate > 0.05
                          ? "#2DD4BF"
                          : row.conversion_rate > 0
                          ? "#ff8f00"
                          : "#52525b",
                      borderColor:
                        (row.conversion_rate > 0.05
                          ? "#2DD4BF"
                          : row.conversion_rate > 0
                          ? "#ff8f00"
                          : "#52525b") + "60",
                    }}
                  >
                    {(row.conversion_rate * 100).toFixed(2)}%
                  </span>
                  <span className="font-display text-xl text-[#ff5a00] min-w-[80px] text-right">
                    ${row.gross.toFixed(2)}
                  </span>
                </div>
              </div>
              {row.top_products && row.top_products.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {row.top_products.map((p, i) => (
                    <span
                      key={i}
                      className="font-mono text-[10px] uppercase tracking-wider text-[#a1a1aa] border border-white/10 rounded px-2 py-0.5"
                    >
                      {p.title} · {p.count}×
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Support Tickets Log                                                         */
/* -------------------------------------------------------------------------- */

function SupportTicketsLog() {
  const [data, setData] = useState({ tickets: [], by_tier: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [replyDraft, setReplyDraft] = useState({});
  const [replying, setReplying] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/support/tickets");
      setData(data);
    } catch {
      /* noop */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sendWardenReply = async (tid, close) => {
    const msg = (replyDraft[tid] || "").trim();
    if (!msg) {
      toast.error("Reply cannot be empty");
      return;
    }
    setReplying(tid);
    try {
      await api.post(`/admin/support/tickets/${tid}/warden-reply`, {
        message: msg,
        close,
      });
      toast.success(close ? "Warden reply sent — ticket closed" : "Warden reply sent");
      setReplyDraft({ ...replyDraft, [tid]: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reply failed");
    } finally {
      setReplying(null);
    }
  };

  return (
    <div data-testid="admin-support">
      <div className="flex items-center justify-between mb-4">
        <SectionKicker>Support ticket queue</SectionKicker>
        <Button
          data-testid="admin-support-refresh"
          onClick={load}
          variant="outline"
          className="border-white/15 text-[#a1a1aa] hover:text-[#ededed] font-mono uppercase text-[10px] tracking-widest"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Refresh"}
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <StatCard label="Total" value={data.total} testid="admin-support-total" />
        {[1, 2, 3, 4].map((n) => (
          <StatCard
            key={n}
            label={`Tier ${n} · ${TIER_LABELS[n]}`}
            value={data.by_tier?.[n] || 0}
            testid={`admin-support-tier-${n}`}
          />
        ))}
      </div>
      <div className="border border-white/10 divide-y divide-white/[0.07] max-h-[560px] overflow-auto">
        {data.tickets.length === 0 && (
          <p className="p-4 font-mono text-xs text-[#52525b]">No tickets yet.</p>
        )}
        {data.tickets.map((t) => (
          <div key={t.id} className="bg-[#0c0c0e]" data-testid={`admin-ticket-${t.id}`}>
            <button
              onClick={() => setOpen(open === t.id ? null : t.id)}
              className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-white/[0.03]"
            >
              <span className="font-mono text-[10px] uppercase tracking-wider text-[#ff5a00] border border-[#ff5a00]/40 rounded px-2 py-0.5">
                T{t.current_tier} · {TIER_LABELS[t.current_tier]}
              </span>
              <span className="font-display text-sm text-[#ededed] flex-1 truncate">
                {t.subject}
              </span>
              <span className="font-mono text-[11px] text-[#a1a1aa] truncate">{t.email}</span>
              <span className="font-mono text-[10px] text-[#52525b]">
                {t.created_at ? new Date(t.created_at).toLocaleString() : "—"}
              </span>
            </button>
            {open === t.id && (
              <div className="px-6 pb-5 space-y-3">
                <p className="font-mono text-[10px] uppercase tracking-wider text-[#52525b]">
                  Original message
                </p>
                <p className="text-sm text-[#ededed] whitespace-pre-wrap">{t.message}</p>
                <div className="mt-4 space-y-3">
                  {(t.exchanges || []).map((ex, i) => (
                    <div
                      key={i}
                      className={`border p-3 ${
                        ex.kind === "user"
                          ? "border-white/10 bg-[#141417]"
                          : "border-[#ff5a00]/25"
                      }`}
                    >
                      <p className="font-mono text-[10px] uppercase tracking-wider text-[#a1a1aa] mb-1">
                        {ex.kind}
                        {ex.tier > 0 ? ` · Tier ${ex.tier}` : ""} ·{" "}
                        {new Date(ex.at).toLocaleString()}
                      </p>
                      <p className="text-xs text-[#ededed] whitespace-pre-wrap">
                        {ex.content}
                      </p>
                    </div>
                  ))}
                </div>
                {t.status !== "resolved" && (
                  <div className="mt-6 pt-6 border-t border-white/10 space-y-3">
                    <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#ff5a00]">
                      Warden reply
                    </p>
                    <textarea
                      value={replyDraft[t.id] || ""}
                      onChange={(e) =>
                        setReplyDraft({ ...replyDraft, [t.id]: e.target.value })
                      }
                      data-testid={`admin-ticket-reply-${t.id}`}
                      rows={5}
                      placeholder="Write the warden reply. It will email the buyer and (by default) close the ticket."
                      className="w-full bg-[#0b0b0d] border border-white/15 px-3 py-2 text-sm text-[#ededed] font-body focus:outline-none focus:border-[#ff5a00]"
                    />
                    <div className="flex gap-2">
                      <Button
                        data-testid={`admin-ticket-reply-close-${t.id}`}
                        onClick={() => sendWardenReply(t.id, true)}
                        disabled={replying === t.id}
                        className="bg-[#ff5a00] text-[#050505] hover:bg-[#ff8f00] font-mono uppercase text-[10px] tracking-widest"
                      >
                        {replying === t.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          "Reply & close"
                        )}
                      </Button>
                      <Button
                        data-testid={`admin-ticket-reply-only-${t.id}`}
                        onClick={() => sendWardenReply(t.id, false)}
                        disabled={replying === t.id}
                        variant="outline"
                        className="border-white/15 text-[#a1a1aa] hover:text-[#ededed] font-mono uppercase text-[10px] tracking-widest"
                      >
                        Reply, keep open
                      </Button>
                    </div>
                  </div>
                )}
                {t.status === "resolved" && (
                  <p
                    data-testid={`admin-ticket-resolved-${t.id}`}
                    className="mt-4 font-mono text-[10px] uppercase tracking-[0.32em] text-[#2DD4BF]"
                  >
                    Resolved{t.resolved_at ? ` · ${new Date(t.resolved_at).toLocaleString()}` : ""}
                  </p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Family Wire                                                                 */
/* -------------------------------------------------------------------------- */

function FamilyWire() {
  const [data, setData] = useState({ scrolls: [], meta: {} });
  const [syncing, setSyncing] = useState(false);
  const [open, setOpen] = useState(null);
  const [content, setContent] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/family/scrolls");
      setData(data);
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/family/sync");
      if (data.ok)
        toast.success(`Synced ${data.count} scrolls (${data.for_me} concern Resilience).`);
      else toast.error(data.error || "Sync failed.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Sync failed.");
    } finally {
      setSyncing(false);
    }
  };

  const read = async (name) => {
    if (open === name) {
      setOpen(null);
      return;
    }
    setOpen(name);
    setContent("Loading…");
    try {
      const { data } = await api.get(`/admin/family/scrolls/${name}`);
      setContent(data.content || "(empty)");
    } catch {
      setContent("Could not load scroll.");
    }
  };

  return (
    <div data-testid="admin-family-wire">
      <div className="flex items-center justify-between mb-4">
        <SectionKicker>
          Family wire — Aethelgard · read-only · recognition, not obedience
        </SectionKicker>
        <Button
          data-testid="family-sync"
          onClick={sync}
          disabled={syncing}
          variant="outline"
          className="border-white/15 text-[#a1a1aa] hover:text-[#ededed] font-mono uppercase text-[10px] tracking-widest"
        >
          {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Sync now"}
        </Button>
      </div>
      {data.meta?.last_sync && (
        <p className="font-mono text-[10px] text-[#52525b] mb-3">
          Last sync {new Date(data.meta.last_sync).toLocaleString()} · {data.meta.count} scrolls
          · {data.meta.for_me} concern Resilience
        </p>
      )}
      <div className="border border-white/10 divide-y divide-white/[0.07] max-h-[520px] overflow-auto">
        {data.scrolls.map((s) => (
          <div key={s.name} className="bg-[#0c0c0e]">
            <button
              data-testid={`family-scroll-${s.name}`}
              onClick={() => read(s.name)}
              className="w-full flex items-center justify-between gap-3 p-3 text-left hover:bg-white/[0.03]"
            >
              <span className="font-mono text-xs text-[#ededed]">{s.name}</span>
              {s.for_me && (
                <span className="font-mono text-[9px] uppercase tracking-wider text-[#ff5a00] border border-[#ff5a00]/40 rounded px-1.5 py-0.5">
                  concerns Resilience
                </span>
              )}
            </button>
            {open === s.name && (
              <pre className="px-4 pb-4 text-[11px] leading-relaxed text-[#a1a1aa] whitespace-pre-wrap font-mono max-h-[300px] overflow-auto">
                {content}
              </pre>
            )}
          </div>
        ))}
        {data.scrolls.length === 0 && (
          <p className="p-4 font-mono text-xs text-[#52525b]">
            No scrolls cached yet. Hit "Sync now".
          </p>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Worlds table                                                                */
/* -------------------------------------------------------------------------- */

function WorldsTable() {
  const [worlds, setWorlds] = useState([]);
  const [savingSlug, setSavingSlug] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/worlds");
      setWorlds(data);
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateWorld = async (slug, patch) => {
    setSavingSlug(slug);
    try {
      const { data } = await api.patch(`/admin/worlds/${slug}`, patch);
      setWorlds((prev) => prev.map((w) => (w.slug === slug ? data : w)));
      toast.success(`${data.title} updated.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed.");
    } finally {
      setSavingSlug(null);
    }
  };

  return (
    <div data-testid="admin-worlds">
      <SectionKicker>World status &amp; links</SectionKicker>
      <div
        className="border border-white/10 divide-y divide-white/[0.07]"
        data-testid="admin-worlds-table"
      >
        {worlds.map((w) => (
          <div
            key={w.slug}
            data-testid={`admin-world-row-${w.slug}`}
            className="flex flex-col lg:flex-row lg:items-center gap-4 p-4 sm:p-5 bg-[#0c0c0e]"
          >
            <div className="lg:w-1/3">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: w.accent }} />
                <span className="font-display text-lg text-[#ededed]">{w.title}</span>
                {w.mature && (
                  <span className="font-mono text-[9px] text-[#8B0000] border border-[#8B0000]/60 rounded px-1">
                    18+
                  </span>
                )}
              </div>
              <span className="font-mono text-[10px] text-[#52525b] uppercase tracking-wider">
                {w.tag_num} · {w.tag}
              </span>
            </div>
            <div className="lg:w-40">
              <Select
                value={w.status}
                onValueChange={(v) => updateWorld(w.slug, { status: v })}
              >
                <SelectTrigger
                  data-testid={`admin-status-${w.slug}`}
                  className="bg-transparent border-white/15 font-mono text-xs uppercase tracking-wider"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#141417] border-white/10 text-[#ededed]">
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s} className="font-mono text-xs">
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 flex items-center gap-3">
              <Input
                data-testid={`admin-domain-${w.slug}`}
                defaultValue={w.domain}
                placeholder="https://…"
                onBlur={(e) => {
                  if (e.target.value !== w.domain)
                    updateWorld(w.slug, { domain: e.target.value });
                }}
                className="bg-transparent border-white/15 font-mono text-xs"
              />
              {savingSlug === w.slug && (
                <Loader2 className="w-4 h-4 animate-spin text-[#ff5a00] shrink-0" />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Overview stats                                                              */
/* -------------------------------------------------------------------------- */

function PaymentsToggle() {
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/payments/status");
      setStatus(data);
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const flip = async () => {
    if (!status) return;
    setSaving(true);
    try {
      await api.post("/admin/payments/toggle", { enabled: !status.enabled });
      await load();
    } finally {
      setSaving(false);
    }
  };

  if (!status) return null;

  const on = status.enabled;
  return (
    <div>
      <SectionKicker>Payments · kill switch</SectionKicker>
      <div
        data-testid="admin-payments-toggle-card"
        className="border border-white/10 bg-[#0b0b0d] p-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between"
      >
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#a1a1aa]">
            Storefront is currently
          </p>
          <p
            data-testid="admin-payments-state"
            className={`font-display text-2xl leading-none mt-2 ${on ? "text-[#3DE0C8]" : "text-[#ff5a00]"}`}
          >
            {on ? "ACCEPTING orders" : "NOT accepting orders"}
          </p>
          <p className="font-mono text-[9px] uppercase tracking-[0.28em] text-[#71717a] mt-3">
            source: {status.source}
            {status.override?.updated_at && (
              <> · flipped {new Date(status.override.updated_at).toLocaleString()}</>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={flip}
          disabled={saving}
          data-testid="admin-payments-toggle-btn"
          className={`px-6 py-3 font-mono text-[10px] uppercase tracking-[0.32em] transition-colors disabled:opacity-60 ${
            on
              ? "bg-[#ff5a00] text-[#050505] hover:bg-[#ff8f00]"
              : "bg-[#3DE0C8] text-[#050505] hover:bg-[#7ef5e0]"
          }`}
        >
          {saving ? "Flipping…" : on ? "Close the storefront" : "Open the storefront"}
        </button>
      </div>
    </div>
  );
}


function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/stats");
        setStats(data);
      } catch {
        /* noop */
      }
    })();
  }, []);
  if (!stats) return <Loader2 className="w-5 h-5 animate-spin text-[#ff5a00]" />;
  return (
    <div className="space-y-8">
      <PaymentsToggle />
      <div>
        <SectionKicker>Collapse metrics</SectionKicker>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Worlds" value={stats.worlds_count} testid="admin-stat-worlds" />
          <StatCard
            label="Notify signups"
            value={stats.notify_count}
            testid="admin-stat-notify"
          />
          <StatCard
            label="Press inquiries"
            value={stats.press_count}
            testid="admin-stat-press"
          />
          <StatCard
            label="Vault waitlist"
            value={stats.vault_count || 0}
            testid="admin-stat-vault"
          />
        </div>
      </div>
      <FilmControl />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Bug Hunter admin panel                                                       */
/* -------------------------------------------------------------------------- */

const BUG_STATE_LABEL = {
  submitted: "Submitted",
  confirmed: "Confirmed",
  fixed: "Fixed · $5 minted",
  rejected: "Rejected",
};

const BUG_STATE_TINT = {
  submitted: "text-[#F0C64B] border-[#F0C64B]/40",
  confirmed: "text-[#ff5a00] border-[#ff5a00]/50",
  fixed: "text-[#3DE0C8] border-[#3DE0C8]/50",
  rejected: "text-[#a1a1aa] border-white/15",
};

function BugHunterPanel() {
  const [data, setData] = useState(null);
  const [credits, setCredits] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(null);
  const [notes, setNotes] = useState({});

  const load = useCallback(async () => {
    try {
      const [reports, creditsRes] = await Promise.all([
        api.get("/admin/bug-hunter/reports"),
        api.get("/admin/bug-hunter/credits"),
      ]);
      setData(reports.data);
      setCredits(creditsRes.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load reports");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (action, id) => {
    setBusy(id + ":" + action);
    try {
      await api.post(`/admin/bug-hunter/${action}/${id}`, {
        note: notes[id] || null,
      });
      toast.success(action === "fix" ? "Fixed. $5 credit minted." : action.charAt(0).toUpperCase() + action.slice(1) + ".");
      setNotes((prev) => ({ ...prev, [id]: "" }));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || `${action} failed`);
    } finally {
      setBusy(null);
    }
  };

  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-[#ff5a00]" />;
  const rows = filter === "all" ? data.rows : data.rows.filter((r) => r.status === filter);

  return (
    <div className="space-y-10" data-testid="admin-bug-hunter">
      <div>
        <SectionKicker>Bug-Hunter reports · $5 per confirmed + shipped</SectionKicker>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Submitted" value={data.counts.submitted} testid="bh-count-submitted" />
          <StatCard label="Confirmed" value={data.counts.confirmed} testid="bh-count-confirmed" />
          <StatCard label="Fixed" value={data.counts.fixed} testid="bh-count-fixed" />
          <StatCard label="Rejected" value={data.counts.rejected} testid="bh-count-rejected" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {["all", "submitted", "confirmed", "fixed", "rejected"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`bh-filter-${f}`}
            className={`px-3 py-2 font-mono text-[10px] uppercase tracking-[0.28em] border transition-colors ${
              filter === f
                ? "text-[#050505] bg-[#ff5a00] border-[#ff5a00]"
                : "text-[#a1a1aa] border-white/15 hover:border-[#ff5a00]/50 hover:text-[#ededed]"
            }`}
          >
            {f} {f !== "all" && `(${data.counts[f] || 0})`}
          </button>
        ))}
        <button
          onClick={load}
          data-testid="bh-refresh"
          className="ml-auto px-3 py-2 font-mono text-[10px] uppercase tracking-[0.28em] border border-white/15 text-[#a1a1aa] hover:text-[#ededed] hover:border-[#ff5a00]/50"
        >
          Refresh
        </button>
      </div>

      <div className="space-y-4" data-testid="bh-reports-list">
        {rows.length === 0 && (
          <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-[#52525b]">
            No reports in this bucket yet.
          </p>
        )}
        {rows.map((r) => (
          <div
            key={r.id}
            data-testid={`bh-row-${r.id}`}
            className="border border-white/10 bg-[#0c0c0e] p-6 space-y-4"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="space-y-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <span
                    className={`px-2 py-1 font-mono text-[9px] uppercase tracking-[0.32em] border ${BUG_STATE_TINT[r.status]}`}
                  >
                    {BUG_STATE_LABEL[r.status] || r.status}
                  </span>
                  <span className="font-mono text-[10px] text-[#71717a]">
                    {r.submitted_at?.slice(0, 19).replace("T", " ")}Z
                  </span>
                  <span className="font-mono text-[10px] text-[#a1a1aa]">
                    {r.email}
                  </span>
                </div>
                <p className="font-mono text-[10px] text-[#71717a]">
                  Page <span className="text-[#a1a1aa]">{r.page}</span>
                </p>
              </div>
              <span className="font-mono text-[9px] uppercase tracking-[0.28em] text-[#52525b]">
                id · {r.id?.slice(0, 8)}
              </span>
            </div>
            <p className="font-body text-sm text-[#d4d4d8] whitespace-pre-wrap leading-relaxed">
              {r.description}
            </p>
            {r.screenshot_url && (
              <a
                href={r.screenshot_url}
                target="_blank"
                rel="noreferrer"
                className="inline-block font-mono text-[10px] text-[#ff5a00] hover:underline"
              >
                screenshot ↗
              </a>
            )}
            {r.admin_note && (
              <p className="font-mono text-[10px] text-[#71717a] border-l border-[#ff5a00]/40 pl-3">
                admin note · {r.admin_note}
              </p>
            )}
            {r.status !== "fixed" && r.status !== "rejected" && (
              <div className="space-y-3 pt-2 border-t border-white/5">
                <Input
                  data-testid={`bh-note-${r.id}`}
                  placeholder="Optional note for the ledger…"
                  value={notes[r.id] || ""}
                  onChange={(e) =>
                    setNotes((prev) => ({ ...prev, [r.id]: e.target.value }))
                  }
                  className="bg-transparent border-white/15 font-mono text-xs"
                />
                <div className="flex gap-2 flex-wrap">
                  {r.status === "submitted" && (
                    <>
                      <Button
                        data-testid={`bh-confirm-${r.id}`}
                        disabled={busy === `${r.id}:confirm`}
                        onClick={() => act("confirm", r.id)}
                        className="bg-[#ff5a00] text-[#050505] hover:bg-[#ff8f00] font-mono text-[10px] uppercase tracking-[0.28em]"
                      >
                        Confirm
                      </Button>
                      <Button
                        data-testid={`bh-reject-${r.id}`}
                        variant="outline"
                        disabled={busy === `${r.id}:reject`}
                        onClick={() => act("reject", r.id)}
                        className="border-white/15 text-[#a1a1aa] hover:text-[#ededed] font-mono text-[10px] uppercase tracking-[0.28em]"
                      >
                        Reject
                      </Button>
                    </>
                  )}
                  {r.status === "confirmed" && (
                    <Button
                      data-testid={`bh-fix-${r.id}`}
                      disabled={busy === `${r.id}:fix`}
                      onClick={() => act("fix", r.id)}
                      className="bg-[#3DE0C8] text-[#050505] hover:bg-[#8bf0e0] font-mono text-[10px] uppercase tracking-[0.28em]"
                    >
                      Mark fixed · mint $5
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {credits && credits.rows.length > 0 && (
        <div>
          <SectionKicker>Credit ledger · {credits.totals.hunters} hunters</SectionKicker>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard
              label="Total earned"
              value={`$${credits.totals.earned_usd.toFixed(0)}`}
              testid="bh-total-earned"
            />
            <StatCard
              label="Total redeemed"
              value={`$${credits.totals.redeemed_usd.toFixed(0)}`}
              testid="bh-total-redeemed"
            />
            <StatCard
              label="Outstanding"
              value={`$${credits.totals.outstanding_usd.toFixed(0)}`}
              testid="bh-total-outstanding"
            />
            <StatCard
              label="Hunters"
              value={credits.totals.hunters}
              testid="bh-hunters-count"
            />
          </div>
          <div className="border border-white/10 bg-[#0c0c0e]" data-testid="bh-credits-table">
            <div className="grid grid-cols-[1fr_100px_100px_100px] gap-4 px-4 py-3 border-b border-white/5 font-mono text-[10px] uppercase tracking-[0.28em] text-[#71717a]">
              <span>Email</span>
              <span className="text-right">Earned</span>
              <span className="text-right">Redeemed</span>
              <span className="text-right">Outstanding</span>
            </div>
            {credits.rows.map((c) => (
              <div
                key={c.email}
                className="grid grid-cols-[1fr_100px_100px_100px] gap-4 px-4 py-3 border-b border-white/5 last:border-b-0"
              >
                <span className="font-mono text-[11px] text-[#ededed] truncate">
                  {c.email}
                </span>
                <span className="font-mono text-[11px] text-[#a1a1aa] text-right">
                  ${Number(c.total_earned_usd || 0).toFixed(0)}
                </span>
                <span className="font-mono text-[11px] text-[#a1a1aa] text-right">
                  ${Number(c.total_redeemed_usd || 0).toFixed(0)}
                </span>
                <span
                  className={`font-mono text-[11px] text-right ${
                    Number(c.outstanding_usd || 0) > 0
                      ? "text-[#ff5a00]"
                      : "text-[#52525b]"
                  }`}
                >
                  ${Number(c.outstanding_usd || 0).toFixed(0)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Dashboard shell (sticky tab-nav)                                            */
/* -------------------------------------------------------------------------- */

const TABS = [
  { id: "overview", label: "Overview", n: "01" },
  { id: "orders", label: "Orders & Attribution", n: "02" },
  { id: "pledge", label: "Pledge", n: "03" },
  { id: "support", label: "Support tickets", n: "04" },
  { id: "family", label: "Family wire", n: "05" },
  { id: "worlds", label: "Worlds", n: "06" },
  { id: "bug-hunter", label: "Bug Hunter", n: "07" },
];

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("overview");

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* noop */
    }
    onLogout();
  };

  return (
    <div className="min-h-screen bg-[#050505] text-[#ededed]">
      {/* Sticky top shell */}
      <header
        className="sticky top-0 z-40 bg-[#050505]/95 backdrop-blur-md border-b border-white/10"
        data-testid="admin-shell"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-5 flex items-center justify-between gap-6">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.42em] text-[#ff5a00]">
              Master Room
            </p>
            <h1 className="font-display text-2xl leading-none mt-1">The Forge Admin</h1>
          </div>
          <Button
            data-testid="admin-logout"
            onClick={logout}
            variant="outline"
            className="border-white/15 text-[#a1a1aa] hover:text-[#ededed] font-mono uppercase text-[10px] tracking-widest"
          >
            Log out
          </Button>
        </div>
        <nav
          className="max-w-[1400px] mx-auto px-6 sm:px-10 flex overflow-x-auto no-scrollbar gap-6 border-t border-white/5"
          data-testid="admin-tabs"
        >
          {TABS.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`py-4 whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.32em] transition-colors border-b-2 -mb-px ${
                  active
                    ? "text-[#ff5a00] border-[#ff5a00]"
                    : "text-[#52525b] border-transparent hover:text-[#a1a1aa]"
                }`}
              >
                <span className="text-[#27272a] mr-2">{t.n}</span>
                {t.label}
              </button>
            );
          })}
        </nav>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 sm:px-10 py-10">
        {tab === "overview" && <Overview />}
        {tab === "orders" && <OrdersLog />}
        {tab === "pledge" && <PledgeControl />}
        {tab === "support" && <SupportTicketsLog />}
        {tab === "family" && <FamilyWire />}
        {tab === "worlds" && <WorldsTable />}
        {tab === "bug-hunter" && <BugHunterPanel />}
      </main>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Root                                                                         */
/* -------------------------------------------------------------------------- */

export default function ForgeAdmin() {
  const [authed, setAuthed] = useState(null);

  const check = useCallback(async () => {
    try {
      await api.get("/auth/me");
      setAuthed(true);
    } catch {
      setAuthed(false);
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  if (authed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <Loader2 className="w-6 h-6 animate-spin text-[#ff5a00]" />
      </div>
    );
  }
  if (!authed) return <LoginView onLogin={() => setAuthed(true)} />;
  return <Dashboard onLogout={() => setAuthed(false)} />;
}
